import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '_clean-code/api/hooks/useAuth';
import { useDocsAccess } from '_clean-code/api/hooks/useDocsAccess';
import { DOCS_ORIGIN } from '_clean-code/api/docs/docsHandoff';
import {
  clearDocsRequest,
  docsGateUrl,
  parseDocsRequest,
  saveDocsRequest
} from './docsAccessRequest';

function leaveToGate(returnTo: string) {
  const gate = docsGateUrl(returnTo);
  if (gate) window.location.replace(gate);
}

export function useDocsLogin() {
  const { isAuthenticated, isLoading } = useAuth();
  const [checkingSession] = useState(
    () => new URLSearchParams(window.location.search).get('check') === '1'
  );
  const [request] = useState(() =>
    checkingSession
      ? parseDocsRequest(window.location.search)
      : saveDocsRequest(window.location.search)
  );
  const started = useRef(false);
  const access = useDocsAccess();
  const { mutate } = access;
  const open = useCallback(() => {
    if (!request) return;
    mutate(request, {
      onSuccess: (url) => {
        clearDocsRequest();
        window.location.replace(url);
      },
      onError: () => {
        // A failed handoff must not keep sending protected routes back here.
        clearDocsRequest();
        if (checkingSession) leaveToGate(request.returnTo);
      }
    });
  }, [request, mutate, checkingSession]);
  useEffect(() => {
    if (!DOCS_ORIGIN || isLoading || started.current) return;
    if (checkingSession && (!request || !isAuthenticated)) {
      started.current = true;
      leaveToGate(request?.returnTo || '/guides');
    } else if (isAuthenticated && request) {
      started.current = true;
      open();
    }
  }, [isAuthenticated, isLoading, request, open, checkingSession]); // The ref prevents duplicate handoffs during auth updates.
  return {
    isAuthenticated,
    isLoading,
    checkingSession,
    request,
    failed: !DOCS_ORIGIN || access.isError,
    pending: access.isPending,
    retry: open,
    cancel: clearDocsRequest
  };
}
